// signalEngine.js
// Phase 3 order-flow entry confirmation.

import { ZONE_PROXIMITY_TEST_PIPS } from "./zoneShared";

export const PRE_ZONE_BUFFER_PIPS = 10;
const EXECUTION_BUFFER_PIPS = ZONE_PROXIMITY_TEST_PIPS;
const SPIKE_BODY_MULTIPLIER = 2;
const SPIKE_RANGE_MULTIPLIER = 1.35;

function normalizeMarket(market) {
  return String(market || "").toUpperCase();
}

function isZoneFailed(zone) {
  if (!zone) {
    return false;
  }

  return zone.valid === false || zone.failed === true;
}

function buildZoneMeta(zone) {
  return {
    zoneId: zone?.id || null,
    zoneHigh: zone?.high ?? null,
    zoneLow: zone?.low ?? null,
    activeZone: zone?.label || zone?.name || zone?.type || null,
    activeZoneSource: "ZONE",
  };
}

function getAverageBody(candles) {
  const sample = candles.slice(-20);

  if (!sample.length) {
    return 0;
  }

  return (
    sample.reduce(
      (sum, candle) => sum + Math.abs((candle.close ?? 0) - (candle.open ?? 0)),
      0
    ) / sample.length
  );
}

function getAverageRange(candles) {
  const sample = candles.slice(-20);

  if (!sample.length) {
    return 0;
  }

  return (
    sample.reduce(
      (sum, candle) => sum + Math.abs((candle.high ?? 0) - (candle.low ?? 0)),
      0
    ) / sample.length
  );
}

export function detectPhase3Signal({
  candles,
  zoneState,
  market,
}) {
  const normalizedMarket = normalizeMarket(market);
  const activeZone = zoneState;
  const zoneMeta = buildZoneMeta(activeZone);

  if (!activeZone?.exists || isZoneFailed(activeZone)) {
    return { state: "WAIT", reason: "No valid zone" };
  }

  const last = candles[candles.length - 1];
  const zoneHigh = activeZone.high;
  const zoneLow = activeZone.low;
  const avgBody = getAverageBody(candles);
  const avgRange = getAverageRange(candles);
  const price = last.close;
  const executionHigh = zoneHigh + EXECUTION_BUFFER_PIPS;
  const executionLow = zoneLow - EXECUTION_BUFFER_PIPS;
  const failed =
    normalizedMarket === "BOOM"
      ? price < executionLow
      : price > executionHigh;

  if (failed) {
    return {
      state: "FAIL",
      reason: "Zone crossed beyond execution range",
      ...zoneMeta,
    };
  }

  const insideExecutionRange =
    normalizedMarket === "BOOM"
      ? price <= zoneHigh && price >= executionLow
      : price >= zoneLow && price <= executionHigh;
  const armLevel =
    normalizedMarket === "BOOM"
      ? zoneHigh + PRE_ZONE_BUFFER_PIPS
      : zoneLow - PRE_ZONE_BUFFER_PIPS;
  const isArmed =
    normalizedMarket === "BOOM"
      ? price <= armLevel && price > zoneHigh
      : price >= armLevel && price < zoneLow;

  if (insideExecutionRange) {
    const body = Math.abs(last.close - last.open);
    const candleRange = Math.abs(last.high - last.low);
    const isSpike =
      body >= avgBody * SPIKE_BODY_MULTIPLIER ||
      candleRange >= avgRange * SPIKE_RANGE_MULTIPLIER;
    const spikeDirectionOK =
      (normalizedMarket === "BOOM" && last.close > last.open) ||
      (normalizedMarket === "CRASH" && last.close < last.open);

    if (isSpike && spikeDirectionOK) {
      return {
        state: "SPIKE",
        signal: "ENTRY",
        direction: normalizedMarket === "BOOM" ? "BUY" : "SELL",
        price: last.close,
        time: last.time,
        reason: "Spike candle detected",
        ...zoneMeta,
      };
    }

    return {
      state: "WATCH",
      reason: "Zone touched, waiting for spike",
      ...zoneMeta,
    };
  }

  if (isArmed) {
    return {
      state: "ARMED",
      reason: "Price in pre-zone buffer",
      ...zoneMeta,
    };
  }

  return {
    state: "WAIT",
    reason: "Waiting for price to reach zone",
    ...zoneMeta,
  };
}
